import { useCallback } from 'react';
import { graphql, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { showWarningNotification } from 'src/components/widgets/notificationToaster/notificationToaster';
import { useAICreditsPolicyGate } from 'src/components/widgets/aiCreditsPolicy/aiCreditsPolicyDialog';
import { useAIStudioCreditsDialog } from '../components/dialogs/aiStudioNoCreditsDialog';
import {
    ServiceAvailabilityResult,
    ServicesWithCredits,
    useGenerationPreflightAvailabilityQuery
} from './__generated__/useGenerationPreflightAvailabilityQuery.graphql';

/**
 * All AI Studio tools share the availability of the AI_STUDIO service. With the AI credit model it answers the
 * common AI authorization: a paying seller is always available, a trial or a Shopify development store only while
 * its one-time free AI credits last.
 */
const AI_STUDIO_POOL_SERVICE: ServicesWithCredits = 'AI_STUDIO';

export interface PreflightOptions {
    /**
     * Overrides the no-credits dialog shown on NO_CREDITS. When provided it is invoked instead of the shared
     * AI Studio dialog, letting a specific flow (e.g. product-setup side images) present its own no-credits modal.
     */
    onNoCredits?: () => void;
}

export interface UseGenerationPreflightReturn {
    /**
     * First the AI credits policy: until the seller accepts it a blocking dialog opens, and onReady is not invoked
     * if the user leaves it. Then resolves the seller's AI availability via serviceAvailabilityStatus and gates
     * onReady on it:
     * - AVAILABLE: invoke onReady immediately.
     * - NO_CREDITS: a trial or a Shopify development store used up its free AI credits; open the no-credits
     *   dialog; onReady is not invoked.
     * - ADDON_NOT_ACTIVE: the subscription is missing or not valid; show a warning toast; onReady is not invoked.
     * - SHOPIFY_DEV_STORE_NOT_ALLOWED: show a warning toast; onReady is not invoked.
     * Any other unavailability reason falls through to onReady so the start mutation surfaces the precise error.
     */
    runWithPreflight: (onReady: () => void | Promise<void>, options?: PreflightOptions) => Promise<void>;
}

export function useGenerationPreflight(): UseGenerationPreflightReturn {
    const relayEnvironment = useRelayEnvironment();
    const { showNoCreditsDialog } = useAIStudioCreditsDialog();
    const { ensureAICreditsPolicyAccepted } = useAICreditsPolicyGate();

    // Queries the backend for the authoritative availability status of the AI Studio service for the
    // current subscription. Returns null when the request fails so callers can fall back gracefully.
    const fetchAvailabilityStatus = useCallback(async (): Promise<ServiceAvailabilityResult | null> => {
        try {
            const data = await fetchQuery<useGenerationPreflightAvailabilityQuery>(
                relayEnvironment,
                graphql`
                    query useGenerationPreflightAvailabilityQuery($serviceName: ServicesWithCredits!) {
                        serviceAvailabilityStatus(serviceWithCredits: $serviceName)
                    }
                `,
                { serviceName: AI_STUDIO_POOL_SERVICE },
                { fetchPolicy: 'network-only' }
            ).toPromise();
            return data?.serviceAvailabilityStatus ?? null;
        } catch (error) {
            console.error('Failed to check AI Studio service availability:', error);
            return null;
        }
    }, [relayEnvironment]);

    const runWithPreflight = useCallback(
        async (onReady: () => void | Promise<void>, options: PreflightOptions = {}) => {
            // Every AI feature gated here consumes AI credits: the AI credits policy comes first.
            if (!(await ensureAICreditsPolicyAccepted())) return;

            const status = await fetchAvailabilityStatus();

            switch (status) {
                // There is no AI addon to activate and no credit pack to grant any more: the subscription itself
                // is missing or not valid.
                case 'ADDON_NOT_ACTIVE':
                    showWarningNotification({
                        message: T._('Your subscription is not active, so the AI tools are not available.', 'Admin')
                    });
                    return;

                case 'NO_CREDITS':
                    if (options.onNoCredits) options.onNoCredits();
                    else showNoCreditsDialog();
                    return;

                case 'SHOPIFY_DEV_STORE_NOT_ALLOWED':
                    showWarningNotification({
                        message: T._('Your account is currently a development store, so AI Agents Studio is not available.', 'Admin')
                    });
                    return;

                // AVAILABLE and any other unavailability reason fall through to the generation, letting the
                // start mutation surface the precise error if the seller still cannot proceed.
                default:
                    await onReady();
                    return;
            }
        },
        [ensureAICreditsPolicyAccepted, fetchAvailabilityStatus, showNoCreditsDialog]
    );

    return { runWithPreflight };
}
